/**
 * Identifying documents on a professional's record.
 *
 * Two kinds reach these screens: the citizen card, held as a personal document,
 * and the professional order credential — the cédula. Which is which is NOT the
 * `type` alone: both carry `type: 1`, and the `professional` flag is what
 * separates them. That reads as an accident but is the contract, because the
 * original enum spelled the credential as -1 and the server stores its absolute
 * value:
 *
 *   PROFESSIONAL_ORDER = -1   UNSPECIFIED = 0   CITIZEN_CARD = 1
 *   PASSPORT = 2              DRIVERS_LICENSE = 3
 *
 * So `Math.abs(PROFESSIONAL_ORDER) === CITIZEN_CARD`, and only the boolean
 * tells a cédula from a citizen card. The helpers below exist so no screen has
 * to remember that.
 */
export const IDENTIFYING_DOCUMENT_TYPE = {
  PROFESSIONAL_ORDER: -1,
  UNSPECIFIED: 0,
  CITIZEN_CARD: 1,
  PASSPORT: 2,
  DRIVERS_LICENSE: 3
} as const;

export interface IdentifyingDocument {
  /** Spelled `_id` for compatibility with the legacy Mongo shape. */
  _id: string;
  /** True for the cédula, false for a personal identity document. */
  professional: boolean;
  type: number;
  /** The document number itself. */
  documentId: string;
  /** URL of the scanned front. */
  front?: string;
  /** URL of the scanned back. */
  back?: string;
  /** RFC 3339, or an empty string for documents created at signup. */
  expirationDate?: string;
  createdAt?: string;
  /** Equals `createdAt` until the document is first edited. */
  updatedAt?: string;
}

const isType = (document: IdentifyingDocument, type: number) =>
  document.type === Math.abs(type);

export const isProfessionalOrder = (document: IdentifyingDocument) =>
  document.professional &&
  isType(document, IDENTIFYING_DOCUMENT_TYPE.PROFESSIONAL_ORDER);

export const isCitizenCard = (document: IdentifyingDocument) =>
  !document.professional &&
  isType(document, IDENTIFYING_DOCUMENT_TYPE.CITIZEN_CARD);

/**
 * The document in force, out of every one of its kind ever registered.
 *
 * Renewing does not edit: the expiry date is write-once server side, so a
 * replacement is filed as a new document and the old one stays as history. The
 * most recently registered is therefore the current one.
 *
 * Sorted on `createdAt` rather than trusting the order the API returns, and
 * falling back to the last entry when the dates are missing — insertion order
 * is what the map preserves.
 */
export function currentDocument(
  documents: Record<string, IdentifyingDocument> | undefined,
  matches: (document: IdentifyingDocument) => boolean
): IdentifyingDocument | undefined {
  const ofKind = Object.values(documents ?? {}).filter(matches);
  if (ofKind.length === 0) return undefined;

  const dated = ofKind.filter(document => Boolean(document.createdAt));
  if (dated.length === 0) return ofKind[ofKind.length - 1];

  return dated.reduce((latest, document) =>
    (document.createdAt ?? "") > (latest.createdAt ?? "") ? document : latest
  );
}

/** An expiry of `""` is what signup writes, and means the date is unknown. */
export const hasExpirationDate = (document: IdentifyingDocument) =>
  Boolean(document.expirationDate && document.expirationDate.trim().length > 0);
